/**
 * @fileoverview PipelineProgress
 * @description Determinate, server-driven progress for one pipeline run.
 *
 *              Design notes, because progress bars are easy to get wrong:
 *              • The track is *segmented by real cost* — each checkpoint owns
 *                a slice proportional to its weight — so the bar doubles as a
 *                map of the run instead of an abstract 0–100.
 *              • Movement only ever goes forward. Backwards motion reads as a
 *                failure even when the run is healthy.
 *              • A slow checkpoint still animates, but only within its own
 *                slice and never past it, so the bar stays honest while
 *                staying alive.
 *              • Remaining time is phrased as an estimate and recalibrates
 *                from observed durations rather than repeating a fixed guess.
 *              • Screen readers get a real `progressbar` with a spoken
 *                `aria-valuetext`, plus a polite live region for stage
 *                changes. `prefers-reduced-motion` drops the shimmer and the
 *                easing.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, Collapse,
} from '@mui/material';
import {
  Activity, AlertTriangle, Check, ChevronDown, ChevronUp, CircleDashed,
  Loader2, Timer, X,
} from 'lucide-react';
import { formatDuration, formatEta } from '../utils/format';

const TEAL = '#0097A9';
const NAVY = '#01426A';

const KIND_LABEL = {
  soap_xml: 'SOAP',
  rest_json: 'REST',
  sql_query: 'SQL',
};

const STATUS_STYLE = {
  pending: { color: '#94a3b8', bg: '#f1f5f9', Icon: CircleDashed },
  running: { color: TEAL, bg: '#ecfeff', Icon: Loader2 },
  completed: { color: '#00875A', bg: '#ecfdf5', Icon: Check },
  error: { color: '#dc2626', bg: '#fef2f2', Icon: AlertTriangle },
  skipped: { color: '#94a3b8', bg: '#f1f5f9', Icon: CircleDashed },
};

/* ── One node in the stage rail ── */
function StageNode({ label, sublabel, status, detail, flex }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.pending;
  const { Icon } = style;
  const spinning = status === 'running';

  return (
    <Stack spacing={0.5} sx={{ flex, minWidth: 0 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 22, height: 22, borderRadius: '7px', flexShrink: 0,
            bgcolor: style.bg, color: style.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            ...(spinning && {
              animation: 'cpc-spin 1s linear infinite',
              '@keyframes cpc-spin': { to: { transform: 'rotate(360deg)' } },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }),
          }}
        >
          <Icon size={13} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            noWrap
            sx={{ fontSize: '0.78rem', fontWeight: 700, color: status === 'pending' ? '#94a3b8' : NAVY }}
          >
            {label}
          </Typography>
          {sublabel && (
            <Typography noWrap sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, letterSpacing: '0.04em' }}>
              {sublabel}
            </Typography>
          )}
        </Box>
      </Stack>
      <Typography noWrap sx={{ fontSize: '0.68rem', color: style.color, fontWeight: 600, pl: '30px' }}>
        {detail}
      </Typography>
    </Stack>
  );
}

const PipelineProgress = ({
  run,
  events,
  percent,
  elapsedMs,
  active,
  onCancel,
}) => {
  const [logOpen, setLogOpen] = useState(false);

  const plan = useMemo(() => run?.plan || [], [run]);
  const states = run?.checkpointStates || {};
  const progress = run?.progress || {};

  /* Segment widths mirror the weights the server used to compute `percent`,
     so a tick mark always lines up with a real checkpoint boundary. */
  const segments = useMemo(() => {
    const weights = [...plan.map((p) => p.weight || 1), 1];
    const total = weights.reduce((a, b) => a + b, 0) || 1;
    return weights.map((w) => (w / total) * 100);
  }, [plan]);

  const compareState = useMemo(() => {
    if (run?.status === 'completed') return 'completed';
    if (run?.status === 'failed') return 'error';
    if (progress.phase === 'compare.started') return 'running';
    return 'pending';
  }, [run?.status, progress.phase]);

  if (!run) return null;

  const failed = run.status === 'failed';
  const cancelled = run.status === 'cancelled';
  const done = run.status === 'completed';
  const shown = Math.min(100, Math.max(0, percent));
  const statusLine = failed
    ? run.error || 'Run failed.'
    : cancelled
      ? 'Run cancelled.'
      : progress.message || 'Starting…';

  const valueText = `${Math.round(shown)} percent. ${statusLine}${
    active ? ` ${formatEta(progress.etaMs)}.` : ''
  }`;

  const accent = failed ? '#dc2626' : cancelled ? '#64748b' : done ? '#00875A' : TEAL;

  return (
    <Paper
      elevation={0}
      sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid #e5e7eb', bgcolor: '#fff' }}
    >
      {/* Heading */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        mb={1.75}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Activity size={18} color={accent} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} color={NAVY} noWrap>
              {run.context?.flowName || 'Pipeline run'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              Flight {run.context?.flight} · {run.context?.date} · {run.context?.station}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Chip
            size="small"
            icon={<Timer size={12} />}
            label={formatDuration(elapsedMs)}
            sx={{ bgcolor: '#f8fafc', color: '#475569', fontWeight: 700, '& .MuiChip-icon': { color: '#64748b' } }}
          />
          {active && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<X size={14} />}
              onClick={onCancel}
              sx={{
                borderColor: '#cbd5e1', color: '#475569', borderRadius: '8px',
                textTransform: 'none', fontWeight: 600,
              }}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Track */}
      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(shown)}
        aria-valuetext={valueText}
        aria-label="Pipeline check progress"
        sx={{ position: 'relative', height: 14, borderRadius: '999px', bgcolor: '#eef2f6', overflow: 'hidden' }}
      >
        <Box
          sx={{
            position: 'absolute', inset: 0, width: `${shown}%`,
            borderRadius: '999px',
            background: failed
              ? 'linear-gradient(90deg,#f87171 0%,#dc2626 100%)'
              : cancelled
                ? 'linear-gradient(90deg,#cbd5e1 0%,#94a3b8 100%)'
                : `linear-gradient(90deg,${NAVY} 0%,${TEAL} 55%,#4ade80 100%)`,
            transition: 'width 120ms linear',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
        {active && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute', inset: 0, width: `${shown}%`, borderRadius: '999px',
              background:
                'linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.45) 50%,transparent 100%)',
              backgroundSize: '160px 100%',
              animation: 'cpc-shimmer 1.4s linear infinite',
              '@keyframes cpc-shimmer': {
                from: { backgroundPosition: '-160px 0' },
                to: { backgroundPosition: '260px 0' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', background: 'none' },
            }}
          />
        )}
        {/* Boundary ticks: one per checkpoint hand-off. */}
        {segments.slice(0, -1).map((_, i) => {
          const left = segments.slice(0, i + 1).reduce((a, b) => a + b, 0);
          return (
            <Box
              key={i}
              aria-hidden
              sx={{
                position: 'absolute', top: 0, bottom: 0, left: `${left}%`,
                width: '2px', bgcolor: 'rgba(255,255,255,0.75)',
              }}
            />
          );
        })}
      </Box>

      {/* Status + percentage */}
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" mt={1} spacing={2}>
        <Typography
          aria-live="polite"
          sx={{ fontSize: '0.78rem', fontWeight: 600, color: failed ? '#dc2626' : '#475569', minWidth: 0 }}
        >
          {statusLine}
        </Typography>
        <Stack direction="row" spacing={1.25} alignItems="baseline" flexShrink={0}>
          {active && (
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>
              {formatEta(progress.etaMs)}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: accent, fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(shown)}%
          </Typography>
        </Stack>
      </Stack>

      {/* Stage rail */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        mt={2}
        divider={<Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', bgcolor: '#e5e7eb' }} />}
      >
        {plan.map((checkpoint, index) => {
          const state = states[checkpoint.id] || {};
          const detail =
            state.status === 'completed'
              ? `${state.crewCount ?? 0} crew · ${formatDuration(state.durationMs)}`
              : state.status === 'error'
                ? state.error || 'failed'
                : state.status === 'running'
                  ? 'querying…'
                  : 'waiting';
          return (
            <StageNode
              key={checkpoint.id}
              flex={segments[index]}
              label={checkpoint.label}
              sublabel={`${checkpoint.short} · ${KIND_LABEL[checkpoint.kind] || checkpoint.kind}`}
              status={state.status || 'pending'}
              detail={detail}
            />
          );
        })}
        <StageNode
          flex={segments[segments.length - 1]}
          label="Compare hops"
          sublabel="VERDICT"
          status={compareState}
          detail={
            done ? run.result?.verdict || 'done' : compareState === 'running' ? 'comparing…' : 'waiting'
          }
        />
      </Stack>

      {/* Live log */}
      <Box mt={1.5}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Button
            size="small"
            onClick={() => setLogOpen((open) => !open)}
            endIcon={logOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b', fontSize: '0.75rem', px: 0.5 }}
            aria-expanded={logOpen}
          >
            {logOpen ? 'Hide' : 'Show'} activity ({events.length})
          </Button>
        </Stack>
        <Collapse in={logOpen} unmountOnExit>
          <Box
            sx={{
              mt: 0.5, maxHeight: 180, overflowY: 'auto', borderRadius: '10px',
              border: '1px solid #e5e7eb', bgcolor: '#f8fafc', p: 1.25,
            }}
          >
            {events.map((event) => (
              <Stack key={event.seq} direction="row" spacing={1} sx={{ py: 0.25 }}>
                <Typography
                  sx={{ fontFamily: 'monospace', fontSize: '0.66rem', color: '#94a3b8', flexShrink: 0 }}
                >
                  {String(Math.round(event.percent)).padStart(3, ' ')}%
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#475569' }}>{event.message}</Typography>
              </Stack>
            ))}
            {!events.length && (
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>No activity yet.</Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default PipelineProgress;
